import { Injectable } from '@angular/core';
import { RestService } from './rest.service';
import { RouteService } from './route.service';
import { PageDrawing } from '../models/page-drawing';
import { DrawAction, DrawOptions } from '../models/draw-action';
import { FigureDrawAction } from '../models/draw-actions/figure-draw-action';
import { Colorable } from '../models/colorable';
import { ColorPickerOptions } from '../../environments/configs/color-picker-options';
import { StampDrawAction } from '../models/draw-actions/stamp-draw-action';
import { TextDrawAction } from '../models/draw-actions/text-draw-action';
import { CommunicationService } from './communication.service';
import { environment } from '../../environments/environment';
import { ApplicationStateService } from './application-state.service';
import { PageDrawingFactory } from '../models/factories/page-drawing-factory';
import { CrudService } from './crud.service';
import { TechnicalFeature } from '@lemberger/lemberger-am';
import { PageService } from './page.service';

declare const fabric;

@Injectable({
    providedIn: 'root'
})
export class FabricService {

    currentDrawAction: DrawAction;
    currentColor: Colorable = { color: ColorPickerOptions.colors[0] };
    currentBorderWidth = 3;

    private pageDrawings: Map<number, PageDrawing> = new Map<number, PageDrawing>();
    private pendingDrawings = new Map<number, string>();

    constructor(private communicationService: CommunicationService,
                private applicationStateService: ApplicationStateService,
                private crudService: CrudService,
                private routeService: RouteService,
                private pageService: PageService,
                private restService: RestService) {
    }

    getPageDrawings(): Promise<Array<PageDrawing>> {

        return new Promise<Array<PageDrawing>>(resolve => {

            this.crudService.getAllEntitiesOfBook(PageDrawing, new PageDrawingFactory())
                .then(pageDrawings => {
                    this.setPageDrawings(pageDrawings as Array<PageDrawing>);
                    resolve(pageDrawings as Array<PageDrawing>);
                });
        });
    }

    setPageDrawings(pageDrawings: Array<PageDrawing>): void {
        this.pageDrawings = new Map<number, PageDrawing>();
        pageDrawings.forEach(ele => {
            this.pageDrawings.set(ele.pageId, ele);
        });
    }

    /**
     * Similar to setPageDrawings(pageDrawings) but in addition the passed values are transformed in forehand.
     * This method purpose is to do the transformation and the storing in just one for-Loop.
     * @param objects
     */
    setObjectsToPageDrawings(objects: Array<any>): void {
        this.pageDrawings = new Map<number, PageDrawing>();
        const pageDrawingFactory = new PageDrawingFactory();
        objects.forEach(ele => {
            this.pageDrawings.set(ele.pageId, pageDrawingFactory.buildFromObject(ele));
        });
    }

    /**
     * Checks if a page has a PageDrawing.
     * @returns {boolean}
     */
    pageHasDrawing(pageId: number): boolean {
        return this.pageDrawings.has(pageId);
    }

    /**
     * Gets the PageDrawing of a page
     * @param {number} pageId
     * @returns {PageDrawing}
     */
    getPageDrawingOfPage(pageId: number): PageDrawing {
        return this.pageDrawings.get(pageId);
    }

    /**
     * Gets the PageDrawing of a page
     * @param {number} pageId
     * @returns {PageDrawing}
     */
    getPageDrawingOfPageByIndex(pageIndex: number): PageDrawing {
        return this.pageDrawings.get(this.pageService.getPageIdByIndex(pageIndex));
    }

    /**
     * Saves a PageDrawing on the server.
     * @param {PageDrawing} pageDrawing
     * @param canvas
     * @param canvasWidth
     * @param canvasHeight
     */
    savePageDrawing(pageDrawing: PageDrawing, canvas, canvasWidth: number, canvasHeight: number): void {

        const copy = canvas.toJSON();
        copy.objects.forEach((drawElement, index) => {
            this.scaleCanvasElement(drawElement, canvasWidth, canvasHeight);

            // We have to differ between stamping path objects and all other paths in the PageCanvasComponent.
            // Therefore the filed stamping stores if the current path is from a stamping.
            if (canvas._objects[index] &&
                canvas._objects[index].type === 'path' &&
                canvas._objects[index].stamping) {
                drawElement.stamping = true;
            }
        });
        pageDrawing.fabricObject = copy;
        pageDrawing.fabricCanvas = JSON.stringify(pageDrawing.fabricObject);

        if (!pageDrawing.id) {
            this.saveDrawing(pageDrawing);
        } else {
            this.updateDrawing(pageDrawing);
        }
    }

    /**
     * Execute the current DrawAction
     */
    executeCurrentDrawAction(canvas: any, drawOptions: DrawOptions, pageDrawing?: PageDrawing): any {

        let returnVal: any;

        if (!this.currentDrawAction) {
            canvas.isDrawingMode = false;

            return;
        }

        if (this.currentDrawAction instanceof FigureDrawAction) {

            const newCanvasElement = this.currentDrawAction.createCanvasElement(drawOptions);

            if (!(this.currentDrawAction instanceof TextDrawAction) ||
                drawOptions.text) {
                canvas.add(newCanvasElement);
            }

            returnVal = newCanvasElement;

            if (!(this.currentDrawAction instanceof TextDrawAction) ||
                drawOptions.text) {
                this.currentDrawAction = undefined;
            }
        }

        if (this.currentDrawAction instanceof StampDrawAction) {

            returnVal = { stamp: true };
            fabric.loadSVGFromURL((this.currentDrawAction as StampDrawAction).svgURL, (objects, options) => {

                const size = 30;
                const shape = fabric.util.groupSVGElements(objects, options);
                shape.set({
                    left: drawOptions.stampingPosition.x - size / 2,
                    top: drawOptions.stampingPosition.y - size / 2,
                    width: size,
                    height: size,
                    fill: drawOptions.color.getColorString(),
                    borderColor: DrawAction.defaults.borderColor,
                    cornerColor: DrawAction.defaults.cornerColor,
                    cornerSize: DrawAction.defaults.cornerSize,
                    transparentCorners: DrawAction.defaults.transparentCorners,
                    stamping: true
                });
                shape.setCoords();
                canvas.add(shape);
                canvas.renderAll();
                if (pageDrawing) {
                    this.savePageDrawing(pageDrawing, canvas, canvas.getWidth(), canvas.getHeight());
                }
                this.currentDrawAction = undefined;
            });
        }

        return returnVal;
    }

    setCurrentDrawAction(drawAction: DrawAction): void {
        this.currentDrawAction = drawAction;
    }

    getCurrentDrawAction(): DrawAction {
        return this.currentDrawAction;
    }

    getCurrentColor(): Colorable {
        return this.currentColor;
    }

    setCurrentColor(color: Colorable): void {
        this.currentColor = color;
    }

    getCurrentBorderWidth(): number {
        return this.currentBorderWidth;
    }

    setCurrentBorderWidth(borderWidth: number): void {
        this.currentBorderWidth = borderWidth;
        this.communicationService.changeDrawModeBorderWidth(borderWidth);
    }

    saveDrawing(pageDrawing: PageDrawing): void {
        const route = this.routeService
            .getRelativeRoute(environment.endpointUrls.pageDrawings + environment.crudEndpointsPostfixes.create,
                { bookId: this.applicationStateService.bookId });

        const stringifiedDrawing = JSON.stringify(pageDrawing);

        if (this.pendingDrawings.has(pageDrawing.pageId)) {
            this.pendingDrawings.set(pageDrawing.pageId, stringifiedDrawing);

            return;
        }

        this.pendingDrawings.set(pageDrawing.pageId, stringifiedDrawing);

        this.restService.post(route, pageDrawing)
            .then(result => {

                const pendingDrawingAsString = this.pendingDrawings.get(pageDrawing.pageId);
                const drawingHasChanged = stringifiedDrawing !== pendingDrawingAsString;

                pageDrawing.id = result.id;
                // pageDrawing.fabricCanvas = JSON.parse(result.fabricCanvas);
                this.pageDrawings.set(pageDrawing.pageId, pageDrawing);
                if (drawingHasChanged) {
                    const updatedDrawing = JSON.parse(pendingDrawingAsString);
                    updatedDrawing.id = result.id;
                    this.updateDrawing(updatedDrawing);
                }
                this.pendingDrawings.delete(pageDrawing.pageId);
                this.crudService.logDirect(TechnicalFeature.Drawings);
            });
    }

    updateDrawing(pageDrawing: PageDrawing): void {
        const route = this.routeService
            .getRelativeRoute(environment.endpointUrls.pageDrawings + environment.crudEndpointsPostfixes.update,
                { bookId: this.applicationStateService.bookId, id: pageDrawing.id });
        this.restService.patch(route, pageDrawing)
            .then(result => {
                pageDrawing.id = result.id;
                // pageDrawing.fabricCanvas = JSON.parse(result.fabricCanvas);
                this.pageDrawings.set(pageDrawing.pageId, pageDrawing);
                this.crudService.logDirect(TechnicalFeature.Drawings);
            });
    }

    /**
     * Scales a single fabric.Canvas Element
     * @param canvasElement
     * @param canvasWidth
     * @param canvasHeight
     */
    private scaleCanvasElement(canvasElement: any, canvasWidth: number, canvasHeight: number): void {

        canvasElement.left = canvasElement.left / canvasWidth;
        canvasElement.top = canvasElement.top / canvasHeight;
        canvasElement.width = canvasElement.width / canvasWidth;
        canvasElement.height = canvasElement.height / canvasHeight;

        if (canvasElement.radius) {
            canvasElement.radius = canvasElement.radius / canvasWidth;
        }

        if (canvasElement.x2) {
            canvasElement.width = canvasElement.width * canvasWidth;
            canvasElement.height = canvasElement.height * canvasHeight;
            canvasElement.x1 = canvasElement.x1 / canvasWidth;
            canvasElement.x2 = canvasElement.x2 / canvasWidth;
            canvasElement.y1 = canvasElement.y1 / canvasHeight;
            canvasElement.y2 = canvasElement.y2 / canvasHeight;
        }

        if (canvasElement.path) {

            canvasElement.width = canvasElement.width * canvasWidth;
            canvasElement.height = canvasElement.height * canvasHeight;
            canvasElement.path.forEach(pathElement => {
                switch (pathElement[0]) {
                    case 'L':
                    case 'M':
                        pathElement[1] = pathElement[1] / canvasWidth;
                        pathElement[2] = pathElement[2] / canvasHeight;
                        break;
                    case 'Q':
                        pathElement[1] = pathElement[1] / canvasWidth;
                        pathElement[2] = pathElement[2] / canvasHeight;
                        pathElement[3] = pathElement[3] / canvasWidth;
                        pathElement[4] = pathElement[4] / canvasHeight;
                        break;
                    case 'C':
                        pathElement[1] = pathElement[1] / canvasWidth;
                        pathElement[2] = pathElement[2] / canvasHeight;
                        pathElement[3] = pathElement[3] / canvasWidth;
                        pathElement[4] = pathElement[4] / canvasHeight;
                        pathElement[5] = pathElement[5] / canvasWidth;
                        pathElement[6] = pathElement[6] / canvasHeight;
                        break;
                    default:
                        break;
                }
            });
        }

        if (canvasElement.setCoords) {
            canvasElement.setCoords();
        }
    }
}
